import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data/mock";
import { FadeUp } from "@/components/shared/RevealText";

export default function QuickServices() {
    return (
        <section id="services" className="relative border-t border-ink/10 bg-white py-24 md:py-32" data-testid="quick-services-section">
            <div className="container-x">
                <div className="mb-16 grid grid-cols-1 items-end gap-8 md:grid-cols-12">
                    <FadeUp className="md:col-span-7">
                        <div className="eyebrow mb-4">Ch. 01 · Services</div>
                        <h2 className="h-display font-display text-[40px] font-black leading-[0.95] text-ink md:text-[64px]">
                            Six front doors
                            <br />
                            to your <span className="serif-italic text-pink">government</span>.
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.1} className="md:col-span-5">
                        <p className="text-[16px] leading-relaxed text-slate-600 md:text-[17px]">
                            Every citizen interaction — requests, ideas, grievances, events, volunteering, tracking —
                            through one thoughtfully designed doorway. No lost tickets. No unclear paths.
                        </p>
                    </FadeUp>
                </div>

                <div className="grid grid-cols-1 gap-px overflow-hidden rounded-[28px] border border-ink/10 bg-ink/10 md:grid-cols-2 lg:grid-cols-3">
                    {SERVICES.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <motion.div
                                key={s.id}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.6, delay: i * 0.06 }}
                                className="group relative bg-white"
                            >
                                <Link
                                    to={s.to}
                                    className="relative flex h-full flex-col justify-between p-8 md:p-10"
                                    data-testid={`quick-service-${s.id}`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="grid h-14 w-14 place-items-center rounded-2xl border border-ink/10 bg-white transition-colors duration-300 group-hover:border-pink group-hover:bg-pink/5">
                                            <Icon className="h-6 w-6 text-ink transition-colors duration-300 group-hover:text-pink" />
                                        </div>
                                        <span className="text-[11px] font-semibold tracking-[0.28em] text-slate-400">
                                            {s.chip}
                                        </span>
                                    </div>
                                    <div className="mt-16">
                                        <h3 className="font-display text-[26px] font-semibold leading-tight tracking-tight text-ink transition-colors duration-300 group-hover:text-ink md:text-[30px]">
                                            {s.title}
                                        </h3>
                                        <p className="mt-3 text-[14px] leading-relaxed text-slate-500 md:text-[15px]">
                                            {s.blurb}
                                        </p>
                                        <div className="mt-8 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.24em] text-ink transition-colors duration-300 group-hover:text-pink">
                                            Open
                                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                        </div>
                                    </div>
                                    {/* Bottom pink line */}
                                    <span className="pointer-events-none absolute bottom-0 left-0 h-[3px] w-0 bg-pink transition-[width] duration-500 group-hover:w-full" />
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
